import {
  CountBasis,
  CurrentInventory,
  StockBreakdown,
} from '../types/inventory.types';
import { StoreProductAttributes } from '../types/products.types';
import { Big, roundStock } from './big-helpers';

/**
 * Stock buckets (docs/plans/orders-and-transfers.md §4.3).
 * `cumulative_quantity` is approved on-hand: sellable + reserved. Pending
 * (waiting for price approval) and blocked stock sit outside it. Rows
 * written before the buckets existed have none and read exactly as before:
 * all of their stock is sellable.
 */

type StockCounters = Partial<
  Pick<
    CurrentInventory,
    | 'cumulative_quantity'
    | 'reserved_quantity'
    | 'pending_quantity'
    | 'blocked_quantity'
    | 'manual_reserved_quantity'
  >
>;

/** True once the stock row carries bucket counters (even at 0). */
export const hasStockBuckets = (row: StockCounters): boolean =>
  row.reserved_quantity != null ||
  row.pending_quantity != null ||
  row.blocked_quantity != null;

/** Sellable stock: cumulative − reserved. */
export const sellableQuantity = (row: StockCounters): number =>
  roundStock(
    new Big(row.cumulative_quantity ?? 0).minus(row.reserved_quantity ?? 0),
  );

/** The buckets of a stock row; on hand = cumulative + pending + blocked. */
export const stockBreakdown = (row: StockCounters): StockBreakdown => {
  const reserved = row.reserved_quantity ?? 0;
  const pending = row.pending_quantity ?? 0;
  const blocked = row.blocked_quantity ?? 0;
  const manualReserved = row.manual_reserved_quantity ?? 0;
  return {
    on_hand: roundStock(
      new Big(row.cumulative_quantity ?? 0).plus(pending).plus(blocked),
    ),
    reserved,
    pending,
    blocked,
    // Only when some is held by hand: other breakdowns stay as they were.
    ...(manualReserved !== 0 ? { manual_reserved: manualReserved } : {}),
  };
};

/**
 * The `cumulative_quantity` a count sets. ON_HAND: what was counted minus
 * the pending and blocked stock the counter also found on the shelf.
 * SELLABLE: the stock to sell plus the reserved stock (held stock stays).
 */
export const cumulativeFromCount = (
  counted: number,
  row: StockCounters,
  basis: CountBasis = CountBasis.ON_HAND,
): number =>
  basis === CountBasis.SELLABLE
    ? roundStock(new Big(counted).plus(row.reserved_quantity ?? 0))
    : roundStock(
        new Big(counted)
          .minus(row.pending_quantity ?? 0)
          .minus(row.blocked_quantity ?? 0),
      );

type StoreProductStock = Pick<
  StoreProductAttributes,
  'inventory' | 'stock_breakdown'
>;

/** What a count should expect to find: on hand, else the sellable stock. */
export const onHandOf = (product: StoreProductStock): number =>
  product.stock_breakdown?.on_hand ?? product.inventory ?? 0;

/** True when some of the stock is reserved, pending or blocked. */
export const hasHeldStock = (
  breakdown: StockBreakdown | null | undefined,
): breakdown is StockBreakdown =>
  !!breakdown &&
  (breakdown.reserved !== 0 ||
    breakdown.pending !== 0 ||
    breakdown.blocked !== 0);
